import React, { useState, useRef } from 'react';
import { useChat } from '../../context/ChatContext';
import Button from '../common/Button';
import Modal from '../common/Modal';

const FolderUploadModal = ({ isOpen, onClose, conversationId }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [destinationPath, setDestinationPath] = useState('');
  const fileInputRef = useRef(null);
  
  const { uploadFolder } = useChat();

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!fileInputRef.current.files || fileInputRef.current.files.length === 0) {
      setUploadError('Please select a zip file to upload');
      return;
    }
    
    const file = fileInputRef.current.files[0];
    
    // Check if the file is a zip file
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setUploadError('Only zip files are supported for folder uploads');
      return;
    }
    
    setIsUploading(true);
    setUploadError(null);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      if (destinationPath) {
        formData.append('destination_path', destinationPath);
      }
      
      const result = await uploadFolder(conversationId, formData);
      setUploadResult(result);
      
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.response?.data?.detail || 'Error uploading folder');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Upload Folder"
      size="md"
    >
      <div className="p-4">
        <p className="mb-4 text-gray-700">
          Upload a zip file containing the folder structure you want to add to this conversation's workspace.
        </p>
        
        <form onSubmit={handleUpload}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Zip File
            </label>
            <input
              type="file"
              accept=".zip"
              ref={fileInputRef}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
              disabled={isUploading}
            />
            <p className="mt-1 text-xs text-gray-500">
              Select a .zip file containing the folder structure to upload
            </p>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Destination Path (Optional)
            </label>
            <input
              type="text"
              value={destinationPath}
              onChange={(e) => setDestinationPath(e.target.value)}
              placeholder="e.g., 'project' or 'src/components'"
              className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm 
                focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              disabled={isUploading}
            />
            <p className="mt-1 text-xs text-gray-500">
              Specify a subdirectory within the workspace where files should be extracted (leave blank for root)
            </p>
          </div>
          
          {uploadError && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded-md">
              {uploadError}
            </div>
          )}
          
          {uploadResult && (
            <div className="mb-4 p-3 bg-green-50 text-green-700 border border-green-200 rounded-md">
              <p><strong>Upload successful!</strong></p>
              <p>Files extracted: {uploadResult.file_count}</p>
              <p>Directories created: {uploadResult.directory_count}</p>
              <p>Total size: {uploadResult.total_size_formatted}</p>
            </div>
          )}
          
          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isUploading}
              isLoading={isUploading}
            >
              Upload
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default FolderUploadModal;